import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { Label } from "@shared/ui/label";
import { Textarea } from "@shared/ui/textarea";
import { FileMinus } from "lucide-react";
import { useGetAllSOPs } from "../../hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { GenericSelect } from "@shared/components/Select";
import { mapToLabelValue } from "@features/mx/device-management/utils";
import { usePostWorkOrderJobCard } from "@features/mx/device-management/hooks";
import { logEvent } from "@shared/analytics/analytics";

interface CreateServiceOrderDialogProps {
  isOpen: boolean;
  onClose: () => void;
  meterNumber: string;
  detail: any
}

const CreateServiceOrderDialog: React.FC<CreateServiceOrderDialogProps> = ({
  isOpen,
  onClose,
  meterNumber,
  detail
}) => {
  const [reason, setReason] = useState("");
  const [sop, setSop] = useState<any>();
  const remoteUtilityId = useRemoteUtilityId();
  const { data: sops } = useGetAllSOPs({ remoteUtilityId });
  const sopList = mapToLabelValue(sops, "name", "id");
  const { mutate: createJobCard } = usePostWorkOrderJobCard();
  const handleSubmit = () => {
     const payload = {
      additional_data: {
        assignment_status: detail?.status,
      device_no: detail?.deviceNo,
      physical_status: detail?.physicalStatus,
      reading_status: detail?.status,
      },
      latitude: detail?.lat,
      longitude: detail?.long,
      premise: detail?.premise,
      remark: reason,
      remote_utility_id: remoteUtilityId,
      request_account_no: detail?.meterNumber,
      request_id: detail?.meterId,
      sop: sop,
    };
    createJobCard(payload, {
      onSuccess: () => {
        logEvent('MX Reading Service Order Raised', {
          reason,
          sop
        })
      }
    });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileMinus className="h-5 w-5" />
            Create Service Order
          </DialogTitle>
        </DialogHeader>
          <div className="py-4">
            <p className="mb-4">
              Create service order to uninstall ghosted meter{" "}
              <strong>{meterNumber}</strong>
            </p>

            <div className="grid gap-4 mb-4">
              <div className="flex items-center gap-4">
                <Label className="text-right">Service Order</Label>
                <select
                  id="priority"
                  value={sop}
                  onChange={(e) => setSop(e.target.value)}
                  className="col-span-3 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                  required
                >
                  {sopList.map((sop) => <option value={sop.value}>{sop.label}</option>)}
                </select>
              </div>

              <div className="grid grid-cols-4 items-start gap-4">
                <Label htmlFor="reason" className="text-right pt-2">
                  Reason
                </Label>
                <Textarea
                  id="reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="col-span-3 min-h-[100px]"
                  placeholder="Provide details about the ghosted meter condition"
                  required
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSubmit}>Create Service Order</Button>
          </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateServiceOrderDialog;
